/* Copyright (c) 2024 MOCA Systems, Inc.  All rights reserved. */

import { Role } from '@touchplan/common';
import * as appStrings from '@touchplan/strings';
import { CommonModule } from '@angular/common';
import { provideComponentStore } from '@ngrx/component-store';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';
import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import {
	combineLatest,
	debounceTime,
	filter,
	first,
	map,
	Observable,
	takeUntil,
	switchMap,
	forkJoin,
	Subject,
} from 'rxjs';

import {
	CommonPredicateOp,
	ICustomFieldFilter,
	ITPFilter,
	TextPredicateOp,
} from 'src/app/app-data/models/TPFilterPredicates';
import { MaterialModule } from 'src/app/common/material/material-module';
import { TPFilterComponent } from 'src/app/common/tp-filter/tp-filter.component';
import { CustomFieldDefinition } from 'src/app/app-data/models/CustomFieldDefinition';
import { SharedUIMaterialModule } from 'src/app/shared/ui/material/shared-ui-material.module';
import { CustomFieldFilterModule } from 'src/app/common/custom-field-filter/components/custom-field-filter.module';
import { MultiPanelModule } from 'src/app/components/multi-panel/multi-panel.module';
import {
	FilterFormCriteria,
	FilterOperator,
	ITicketCustomFields,
	TicketAttachmentOptions,
} from '../../interfaces/plan-tickets-filter.interface';
import { PlanTicketsFilterState } from '../../services/plan-tickets-filter-state.service';
import { PlanTicketsFilterRemoteControlService } from 'src/app/services/plan-tickets-filter-remote-control.service';
import { Swimlane } from 'src/app/app-data/models/Swimlane';
import { ITicketAttachmentList } from 'src/app/app-data/models/Attachment';
import { isFilterValid } from 'src/app/utils/comparison-utils';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { PlanPrintPanelRemoteControlService } from '../../../../services/plan-print-panel-remote-control.service';

const MODULES = [
	CommonModule,
	CustomFieldFilterModule,
	MaterialModule,
	ReactiveFormsModule,
	SharedUIMaterialModule,
	MultiPanelModule,
];

const DEFAULT_TP_FILTER_VALUE = { value: '', operator: CommonPredicateOp.AnyValue };
// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6606
//the tp-filter behaves differently when initialized with a null vs an explicit anyValue
// - a null suppresses the first emission
// - an explicit value does not
// currently unclear why, and what the correct behavior is. May be tp-filter at
// fault, may be this file somehow.
// const REAL_EMPTY_TP_FILTER_VALUE = { value: '', operator: CommonPredicateOp.Ignore };
const EMPTY_TP_FILTER_VALUE = null;
const DESCRIPTION_TP_FILTER_VALUE = { value: '', operator: TextPredicateOp.Contains };
const EMPTY_CUSTOM_FIELD_FILTER_VALUE = null;
// END PLACEHOLDER REPLACE AS PART OF TP-6606

const COMPONENTS = [TPFilterComponent];

interface DefaultFormCriteria {
	description?: ITPFilter;
	fromDate: string;
	toDate: string;
	locationIds: string[];
	roleIds: string[];
	externalScheduleId?: ITPFilter;
	type: string[];
	wbsCode?: ITPFilter;
	ticketStatus: string[];
	ticketAttachments: TicketAttachmentOptions;
	swimlaneIds: string[];
	customField1?: ICustomFieldFilter;
	customField2?: ICustomFieldFilter;
	customField3?: ICustomFieldFilter;
	attachmentComment?: ITPFilter;
}

interface FormDefaults extends DefaultFormCriteria {
	toggleFilterByDescription: boolean;
	toggleFilterByExternalSchedule: boolean;
	toggleFilterByWBS: boolean;
	toggleFilterByCustomFields: boolean;
	filterOperator: FilterOperator.AND;
	toggleFilterByAttachmentComment: boolean;
}

const DEFAULT_FORM_CRITERIA: DefaultFormCriteria = {
	description: DESCRIPTION_TP_FILTER_VALUE,
	fromDate: '',
	toDate: '',
	locationIds: [],
	roleIds: [],
	externalScheduleId: DEFAULT_TP_FILTER_VALUE,
	type: [],
	wbsCode: DEFAULT_TP_FILTER_VALUE,
	ticketAttachments: TicketAttachmentOptions.Any,
	ticketStatus: [],
	swimlaneIds: [],
	customField1: EMPTY_CUSTOM_FIELD_FILTER_VALUE,
	customField2: EMPTY_CUSTOM_FIELD_FILTER_VALUE,
	customField3: EMPTY_CUSTOM_FIELD_FILTER_VALUE,
	attachmentComment: DEFAULT_TP_FILTER_VALUE,
};

const FORM_DEFAULTS: FormDefaults = {
	...DEFAULT_FORM_CRITERIA,
	toggleFilterByDescription: false,
	toggleFilterByExternalSchedule: false,
	toggleFilterByWBS: false,
	toggleFilterByCustomFields: false,
	toggleFilterByAttachmentComment: false,
	filterOperator: FilterOperator.AND,
};

@Component({
	standalone: true,
	selector: 'plan-tickets-filter',
	templateUrl: './plan-tickets-filter.component.html',
	styleUrls: ['./plan-tickets-filter.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [...MODULES, ...COMPONENTS],
	providers: [provideComponentStore(PlanTicketsFilterState)],
})
export class PlanTicketsFilterComponent implements OnInit, OnDestroy {
	private planTicketsFilterState = inject(PlanTicketsFilterState);
	private planTicketsFilterRemoteControlService = inject(PlanTicketsFilterRemoteControlService);
	private planPrintPanelRemoteControlService = inject(PlanPrintPanelRemoteControlService);
	private featureFlagManager = inject(FeatureFlagManager);

	@Input() set projectId(projectId: string) {
		this.planTicketsFilterState.setProjectId(projectId);
	}

	@Input() set planTickets(planTickets: Observable<Map<string, any>>) {
		this.planTicketsFilterState.setPlanTickets(planTickets);
	}

	@Input() set userId(userId: string) {
		this.planTicketsFilterState.setUserId(userId);
	}

	@Input() set locationOptions(locationOptions: Observable<Map<string, Location>>) {
		this.planTicketsFilterState.setLocationOptions(locationOptions);
	}

	@Input() set roleOptions(roleOptions: Observable<Map<string, Role>>) {
		this.planTicketsFilterState.setRoleOptions(roleOptions);
	}

	@Input() set swimlaneOptions(swimlaneOptions: Observable<Map<string, Swimlane>>) {
		this.planTicketsFilterState.setSwimlaneOptions(swimlaneOptions);
	}

	@Input() set customFields(customFields: Observable<Map<string, CustomFieldDefinition>>) {
		this.planTicketsFilterState.setCustomFieldOptions(customFields);
	}

	@Input() set ticketAttachments(ticketAttachments: Observable<ITicketAttachmentList>) {
		this.planTicketsFilterState.setTicketAttachments(ticketAttachments);
	}

	@Input() set ticketCustomFields(ticketCustomFields: ITicketCustomFields) {
		this.planTicketsFilterState.setTicketCustomFields(ticketCustomFields);
	}

	@Input() set quickSearch(quickSearchValue: string) {
		if (typeof quickSearchValue === 'string') {
			this.planTicketsFilterState.setQuickSearch({
				value: quickSearchValue,
				operator: TextPredicateOp.Contains,
			});
		}
	}

	@Output() public readonly matchingTicketsChange = this.planTicketsFilterState.select((state) => ({
		tickets: state.matchingTickets,
	}));

	public appStrings = appStrings;
	public CommonPredicateOp = CommonPredicateOp;
	public filterOperatorControl = new FormControl(FilterOperator.AND);
	public collapseFilterPanel$ = this.planTicketsFilterState.quickSearch$.pipe(
		filter((quickSearch) => quickSearch && quickSearch.value !== ''),
		map(() => true)
	);

	protected locationOptions$ = this.planTicketsFilterState.locationOptions$;
	protected rolesOptions$ = this.planTicketsFilterState.roleOptions$;
	protected swimlaneOptions$ = this.planTicketsFilterState.swimlaneOptions$;
	protected customFieldOptions$ = this.planTicketsFilterState.customFieldOptions$;
	protected filterOperatorOptions$ = this.planTicketsFilterState.filterOperatorOptions$;
	protected ticketTypeOptions$ = this.planTicketsFilterState.ticketTypeOptions$;
	protected ticketStatusOptions$ = this.planTicketsFilterState.ticketStatusOptions$;
	protected ticketAttachmentOptions$ = this.planTicketsFilterState.ticketAttachmentOptions$;

	public formBuilder = inject(FormBuilder);

	// If add/remove a field to the formGroup, make sure to add it to criteriaMap in the PlanTicketsFilterState
	public formGroup = this.formBuilder.group({
		toggleFilterByDescription: [FORM_DEFAULTS.toggleFilterByDescription],
		toggleFilterByExternalSchedule: [FORM_DEFAULTS.toggleFilterByExternalSchedule],
		toggleFilterByWBS: [FORM_DEFAULTS.toggleFilterByWBS],

		toggleFilterByAttachmentComment: [FORM_DEFAULTS.toggleFilterByAttachmentComment],
		attachmentComment: [FORM_DEFAULTS.attachmentComment],

		toggleFilterByCustomFields: [FORM_DEFAULTS.toggleFilterByCustomFields],
		description: [FORM_DEFAULTS.description],
		fromDate: [FORM_DEFAULTS.fromDate],
		toDate: [FORM_DEFAULTS.toDate],
		locationIds: [FORM_DEFAULTS.locationIds],
		roleIds: [FORM_DEFAULTS.roleIds],
		externalScheduleId: [FORM_DEFAULTS.externalScheduleId],
		type: [FORM_DEFAULTS.type],
		wbsCode: [FORM_DEFAULTS.wbsCode],
		ticketAttachments: this.formBuilder.nonNullable.control(FORM_DEFAULTS.ticketAttachments),
		ticketStatus: [FORM_DEFAULTS.ticketStatus],
		swimlaneIds: [FORM_DEFAULTS.swimlaneIds],
		customField1: [FORM_DEFAULTS.customField1],
		customField2: [FORM_DEFAULTS.customField2],
		customField3: [FORM_DEFAULTS.customField3],
	});

	// show the plan print button if feature is enabled
	public isPlanPrintEnabled$ = this.featureFlagManager.getFlag$(DynamicFlagTypes.PLAN_PRINT_ENABLED);

	public onCloseFilter() {
		this.planTicketsFilterRemoteControlService.disableQuickSearch(false);
		this.planTicketsFilterRemoteControlService.closePlanTicketsFilter();
	}

	public onOpenPlanFilter() {
		this.onCloseFilter();
		this.planPrintPanelRemoteControlService.openPrintPlantSettingsControl();
	}

	public onClearSettings() {
		this.formGroup.reset(); // Reset the form, clear all fields, and set their default values as null
		// a single reset probably isn't working because the controls in the formGroup haven't been configured
		// as non-nullable (unverified)
		// https://angular.dev/guide/forms/typed-forms#nullability
		this.formGroup.reset(FORM_DEFAULTS); // Set the default values to our own default values
		this.filterOperatorControl.reset(FORM_DEFAULTS.filterOperator);
	}

	// Convert the form values to the filter
	// The way this appears to have been designed is that all controls are always part of the form
	// even the ones that are hidden and shown. And this function is responsible for "excluding" any hidden ones.
	// But also, this combined checkbox + real control concept is the state of the preferences so the "blank" values
	// still need to be passed in.
	private getFilterCriteriaFromFormValues() {
		return this.formGroup.valueChanges.pipe(
			// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6606
			// apparently this is working around a tp-filter validation race condition <_<
			// where the internal validation doesn't get set until the control is initialized
			debounceTime(500),
			// temporarily disabled
			// enabling it means that flipping a checkbox to false while it's child control is invalid
			// will result in "perma-invalid". Kind of inclined to leave it here, since the current
			// version of this form doesn't actually _need_ to have invalid states
			//filter(() => this.formGroup.valid ),
			// END PLACEHOLDER REPLACE AS PART OF TP-6606
			// include only the fields that are used as Filtering criteria
			map((criteria) => ({
				description: this.behindCheckbox(!!criteria.toggleFilterByDescription, criteria.description),
				externalScheduleId: this.behindCheckbox(
					criteria.toggleFilterByExternalSchedule,
					criteria.externalScheduleId
				),
				wbsCode: this.behindCheckbox(criteria.toggleFilterByWBS, criteria.wbsCode),
				fromDate: criteria.fromDate,
				toDate: criteria.toDate,
				locationIds: criteria.locationIds,
				roleIds: criteria.roleIds,
				type: criteria.type,
				ticketStatus: criteria.ticketStatus,
				ticketAttachments: criteria.ticketAttachments,
				swimlaneIds: criteria.swimlaneIds,
				customField1: this.behindCheckbox(criteria.toggleFilterByCustomFields, criteria.customField1),
				customField2: this.behindCheckbox(criteria.toggleFilterByCustomFields, criteria.customField2),
				customField3: this.behindCheckbox(criteria.toggleFilterByCustomFields, criteria.customField3),
				attachmentComment: this.behindCheckbox(
					criteria.toggleFilterByAttachmentComment,
					criteria.attachmentComment
				),
			}))
		);
	}

	//small readability thing for form -> state conversion, fallback if checkbox is false
	private behindCheckbox<T extends ITPFilter>(checkbox: boolean, realValue: T) {
		return checkbox ? realValue : EMPTY_TP_FILTER_VALUE;
	}

	private setFormValuesFromFilterCriteria(filterCriteria: FilterFormCriteria) {
		this.formGroup.patchValue({
			...filterCriteria,
			// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6606
			// why are we _very specifically_ validating just the description?
			description:
				filterCriteria.description && !!filterCriteria.description.value
					? filterCriteria.description
					: FORM_DEFAULTS.description,
			// END PLACEHOLDER REPLACE AS PART OF TP-6606
		});
	}

	//since we don't store checkbox values, we need to calculate their initial state from the values
	//those checkboxes are hiding. Criteria: if defined and "complete"
	private calcCheckboxValuesFromServerData(filterCriteria: FilterFormCriteria) {
		const isAnyCustomFieldActive =
			(!!filterCriteria?.customField1 && isFilterValid(filterCriteria.customField1)) ||
			(!!filterCriteria?.customField2 && isFilterValid(filterCriteria.customField2)) ||
			(!!filterCriteria?.customField3 && isFilterValid(filterCriteria.customField3));

		return {
			toggleFilterByAttachmentComment:
				!!filterCriteria?.attachmentComment && isFilterValid(filterCriteria.attachmentComment),
			toggleFilterByDescription: !!filterCriteria?.description && isFilterValid(filterCriteria.description),
			toggleFilterByExternalSchedule:
				!!filterCriteria?.externalScheduleId && isFilterValid(filterCriteria.externalScheduleId),
			toggleFilterByWBS: !!filterCriteria?.wbsCode && isFilterValid(filterCriteria.wbsCode),
			toggleFilterByCustomFields: isAnyCustomFieldActive,
		};
	}

	private filterFormCriteriaSubscription() {
		// Subscribe to the filterFormCriteria$ observable to set the form values from the filter criteria
		// until the server data is loaded.
		this.planTicketsFilterState.serverDataLoaded$
			.pipe(
				filter((loaded) => loaded),
				switchMap(() =>
					forkJoin({
						criteria: this.planTicketsFilterState.filterFormCriteria$.pipe(first()),
						operator: this.planTicketsFilterState.selectedFilterOperator$.pipe(first()),
					})
				)
			)
			.subscribe(({ criteria, operator }) => {
				if (!criteria) {
					this.setFormValuesFromFilterCriteria(DEFAULT_FORM_CRITERIA);
				} else {
					this.setFormValuesFromFilterCriteria(
						Object.assign({}, FORM_DEFAULTS, criteria, this.calcCheckboxValuesFromServerData(criteria))
					);
				}
				if (operator !== FORM_DEFAULTS.filterOperator) {
					this.filterOperatorControl.patchValue(operator);
				}
			});
	}

	private listenQuickSearchChanges() {
		combineLatest([
			this.planTicketsFilterState.quickSearch$,
			// Wait for the server data to be loaded before setting the form values from the quick search
			// to avoid overwrite of quicksearch values once the server data is loaded.
			this.planTicketsFilterState.serverDataLoaded$.pipe(filter((loaded) => loaded)),
		])
			.pipe(takeUntil(this.planTicketsFilterState.destroy$))
			.subscribe(([quickSearch]) => {
				if (quickSearch && quickSearch.value !== '') {
					// In "quick search mode," we should deliberately reset filter criteria to default values
					// to ensure that previously selected filters are not applied during the user's quick search.
					this.planTicketsFilterState.setSelectedFilterOperator(FilterOperator.AND);
					this.setFormValuesFromFilterCriteria(
						Object.assign({}, FORM_DEFAULTS, {
							description: quickSearch,
							toggleFilterByDescription: true,
						})
					);
				}
			});
	}

	// This function is triggered as soon as the user interacts with the multi-panel header
	// to expand or collapse the filter panel.
	public userChangedFiltersCollapsedState() {
		// If the user is in "quick search" mode, switching to the "full filter" mode,
		// we need to clear and disable quick search to avoid any conflicts.
		// Here, we check the latest quick search state to determine if the user is currently in "quick search" mode.
		this.planTicketsFilterState.quickSearch$.pipe(first()).subscribe((quickSearch) => {
			// If the user expanded the filter while in "quick search" mode, clear and disable quick search.
			if (quickSearch && quickSearch.value !== '') {
				this.planTicketsFilterState.setQuickSearch(null);
				this.planTicketsFilterRemoteControlService.disableQuickSearch(true);
			}
		});
	}

	public ngOnInit(): void {
		this.planTicketsFilterState.setSelectedFilterOperator(this.filterOperatorControl.valueChanges);
		this.planTicketsFilterState.filterTickets(this.getFilterCriteriaFromFormValues());
		this.filterFormCriteriaSubscription();
		this.listenQuickSearchChanges();
		this.planTicketsFilterState.quickSearch$.pipe(first()).subscribe((quickSearch) => {
			if (!quickSearch || quickSearch.value === '') {
				// Disable quick search because the user started the filter in "full filter" mode
				this.planTicketsFilterRemoteControlService.disableQuickSearch(true);
			}
		});
	}

	public ngOnDestroy(): void {
		this.onCloseFilter();
	}
}
